import axios from 'axios';
import { getAccessToken } from './tokenStorage';

const GO_CHAT_BASE = process.env.REACT_APP_GO_CHAT_BASE_URL || 'http://localhost:8081';
const SPRING_API_BASE = '/api';

// Asks Spring for a fresh refresh token usable against Go's /auth/exchange.
// Spring authorizes via the access token in localStorage; the long-lived refresh token
// itself stays in the HttpOnly cookie and is never exposed to JS.
const fetchChatRefreshToken = async () => {
  const accessToken = getAccessToken();
  if (!accessToken) {
    throw new Error('Not authenticated');
  }
  const response = await axios.post(
    `${SPRING_API_BASE}/auth/chat-token`,
    {},
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      withCredentials: true,
    }
  );
  const payload = response.data?.data ?? response.data;
  const token = payload?.refreshToken ?? payload?.refresh_token;
  if (!token) {
    throw new Error('chat-token response did not include a refresh token');
  }
  return token;
};

// Create axios instance for Go chat service
const goChatApi = axios.create({
  baseURL: GO_CHAT_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Storage key for chat token
const CHAT_TOKEN_KEY = 'chatAccessToken';

// Get chat token from storage
export const getChatToken = () => localStorage.getItem(CHAT_TOKEN_KEY);

// Set chat token in storage
export const setChatToken = (token) => localStorage.setItem(CHAT_TOKEN_KEY, token);

// Clear chat token
export const clearChatToken = () => localStorage.removeItem(CHAT_TOKEN_KEY);

// Decodifica el payload del JWT del chat sin verificar firma. Lo usamos para
// detectar tokens stale: si el `userId` del JWT no matchea con el user
// actualmente logueado en Spring, hay que tirarlo. Sin esto, alguien que
// abrió la app como user A y reentró como user B seguiría hablando como A
// para el servicio Go — Go rechazaría con "Cannot start a chat with yourself"
// cuando B intente abrir una convo consigo mismo (que cree es con A).
const decodeChatTokenPayload = () => {
  const token = getChatToken();
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

// userId encoded en el chat token actual (o null si no hay / es inválido).
export const getChatTokenUserId = () => decodeChatTokenPayload()?.userId ?? null;

// Garantiza que el chat token guardado pertenece al user dado. Si falta,
// está corrupto o es de otro user, lo descarta y pide uno nuevo a Spring+Go.
// Llamar al inicio de cualquier flow que dependa del token (page mount,
// switch de user, etc.) ANTES de usar `goChatApi` o el WebSocket.
export const ensureChatTokenForUser = async (userId) => {
  if (userId == null) throw new Error('ensureChatTokenForUser: userId is required');
  const stored = getChatTokenUserId();
  if (stored == null || String(stored) !== String(userId)) {
    clearChatToken();
    await initializeChat();
  }
};

// Add auth token to requests
goChatApi.interceptors.request.use(
  (config) => {
    const token = getChatToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token expiration with retry-loop protection
goChatApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && !error.config._retried) {
      try {
        const refreshToken = await fetchChatRefreshToken();
        const response = await exchangeChatToken(refreshToken);
        setChatToken(response.chatAccessToken);
        // Mark as retried to prevent infinite loops
        error.config._retried = true;
        error.config.headers.Authorization = `Bearer ${response.chatAccessToken}`;
        return goChatApi(error.config);
      } catch (exchangeError) {
        clearChatToken();
        return Promise.reject(exchangeError);
      }
    }
    return Promise.reject(error);
  }
);

// Exchange Spring refresh token for Go chat token.
// Expected Go response format (no wrapper): { chatAccessToken: "...", expiresIn: ... }
// The Go service at POST /auth/exchange returns the token directly, NOT wrapped in { data: ... }.
export const exchangeChatToken = async (refreshToken) => {
  const response = await axios.post(`${GO_CHAT_BASE}/auth/exchange`, {
    refreshToken,
  });
  return response.data;
};

// Initialize chat - obtain a chat-scoped refresh token from Spring, exchange it with Go, store the chat token.
export const initializeChat = async () => {
  const refreshToken = await fetchChatRefreshToken();
  const response = await exchangeChatToken(refreshToken);
  setChatToken(response.chatAccessToken);
  return response;
};

// Conversation services. Modelo nuevo user-to-user: la convo se identifica
// por el par de participantes, sin producto. `createOrGetConversation` es
// lazy — sin message ni offerAmount sólo resuelve la convo existente; con
// payload, crea (o usa la existente) y deja el primer mensaje persistido.
export const goChatService = {
  // Find or create the conversation with `userId`. `message`, `productId` y
  // `reservationId` son opcionales: si vienen, se persisten como primer
  // mensaje (con referencia a un producto O una reserva). Sin ningún payload,
  // sólo resuelve la convo existente — el UI maneja "no convo aún, virtual
  // hasta el primer send".
  createOrGetConversation: (userId, message, productId, reservationId) =>
    goChatApi.post('/api/conversations', {
      userId: String(userId),
      message,
      productId,
      reservationId,
    }),

  // Get all user's conversations
  getUserConversations: () => goChatApi.get('/api/conversations'),

  // Get specific conversation
  getConversation: (conversationId) => goChatApi.get(`/api/conversations/${conversationId}`),

  // Get conversation messages with pagination
  getConversationMessages: (conversationId, limit = 50, offset = 0) =>
    goChatApi.get(`/api/conversations/${conversationId}/messages`, { params: { limit, offset } }),

  // Mark conversation as read
  markAsRead: (conversationId) => goChatApi.post(`/api/conversations/${conversationId}/read`),
};

// WebSocket connection manager
export class GoChatWebSocket {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this._reconnectTimer = null;
  }

  async connect() {
    // Close any existing connection first
    if (this.socket) {
      this.socket.onclose = null; // Prevent reconnect loop
      this.socket.close();
      this.socket = null;
    }

    let token = getChatToken();

    // If no token, try to get one
    if (!token) {
      await initializeChat();
      token = getChatToken();
    }

    if (!token) {
      throw new Error('Failed to get chat token');
    }

    return new Promise((resolve, reject) => {
      this.socket = new WebSocket(`${process.env.REACT_APP_GO_CHAT_WS_URL || 'ws://localhost:8081/ws'}?token=${token}`);

      this.socket.onopen = () => {
        this.reconnectAttempts = 0;
        this.emit('CONNECTED', {});
        resolve();
      };

      this.socket.onclose = (event) => {
        this.emit('close', event);
        this.attemptReconnect();
      };

      this.socket.onerror = (error) => {
        this.emit('error', error);
        reject(error);
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          // silently ignore
        }
      };
    });
  }

  handleMessage(data) {
    // Emit by message type
    this.emit(data.type, data);
    // Also emit generic message event
    this.emit('message', data);
  }

  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('reconnectFailed');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this.connect().catch(() => {
        // Reconnect failed, will try again
      });
    }, delay);
  }

  disconnect() {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close();
      this.socket = null;
    }
    this.reconnectAttempts = 0;
  }

  send(data) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data));
    }
  }

  // Send a text message
  // productId / reservationId son opcionales — si vienen, van en el payload
  // del WS y quedan embebidos en el mensaje (se renderean como tarjetas
  // clickeables). El frontend manda uno u otro según el contexto del chat.
  sendMessage(conversationId, text, productId, reservationId) {
    const msg = { type: 'MESSAGE', conversationId, text };
    if (productId) msg.productId = String(productId);
    if (reservationId) msg.reservationId = String(reservationId);
    this.send(msg);
  }

  // Send a counter offer
  sendCounterOffer(conversationId, amount) {
    this.send({ type: 'COUNTER_OFFER', conversationId, amount });
  }

  // Accept an offer
  acceptOffer(conversationId) {
    this.send({ type: 'ACCEPT_OFFER', conversationId });
  }

  // Reject an offer
  rejectOffer(conversationId) {
    this.send({ type: 'REJECT_OFFER', conversationId });
  }

  // Mark messages as read
  markAsRead(conversationId) {
    this.send({ type: 'READ', conversationId });
  }

  // Event listener management
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    const listeners = this.listeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.forEach((callback) => callback(data));
    }
  }
}

// Singleton instance
export const goChatSocket = new GoChatWebSocket();

export default goChatApi;
